import {
  GetProductById,
  GetProductsByBatch,
  GetProductsByCollectionId,
  GetCollectionById,
  GetCollectionsByBatch,
  GetCollectionsByTitles,
  GetVariantMetafields,
  SearchProducts,
} from 'lib-queries'

/**
 * A simple client for working with the Storefront GraphQL API.
 */
class StorefrontClient {
  /**
   * @param {string} domain - The domain of the Shopify store.
   * @param {string} accessToken - The access token for the Storefront API.
   * @param {string} apiVersion - The API version to use.
   */
  constructor(
    domain = __AS_GLOBAL__.domain,
    accessToken = __AS_GLOBAL__.storefrontToken,
    apiVersion = '2023-01'
  ) {
    this.endpoint = `https://${domain}/api/${apiVersion}/graphql.json`
    this.headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': accessToken,
    }
  }

  /**
   * Execute a GraphQL query against the endpoint.
   * @param {string} graphQLQuery - The GraphQL query to be executed.
   * @param {Object} variables - The variables for the query.
   * @returns {Promise<Object>} The JSON response from the endpoint.
   */
  async query(graphQLQuery, variables = {}) {
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({
          query: graphQLQuery,
          variables,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(`Shopify GraphQL Error: ${JSON.stringify(errorData)}`)
      }

      return await response.json()
    } catch (error) {
      console.error('An error occurred while querying Shopify:', error)
      throw error
    }
  }

  /**
   * Fetch product data by its ID.
   * @param {string} id - The product ID.
   * @returns {Promise<Object>} The processed product data.
   */
  async getProductById(id) {
    const response = await this.query(GetProductById, {
      id: `gid://shopify/Product/${id}`,
    })
    return cleanUpProductData(response.data)
  }

  /**
   * Fetch variant metafields by variant ID.
   * @param {string} id - The variant ID.
   * @returns {Promise<Object>} The processed variant data.
   */
  async getVariantMetafields(id) {
    const response = await this.query(GetVariantMetafields, {
      id: `gid://shopify/ProductVariant/${id}`,
    })
    return cleanUpMetafields(response.data.node.metafields)
  }

  /**
   * Fetch batch of products data by their IDs.
   * @param {string} ids - The product IDs.
   * @returns {Promise<Object>} The processed products data.
   */
  async getProductsByBatch(ids) {
    const response = await this.query(GetProductsByBatch, {
      ids: ids.map((id) => `gid://shopify/Product/${id}`),
    })
    return response.data.nodes
      .filter(Boolean)
      .map((product) => cleanUpProductData({ node: product }))
  }

  /**
   * Fetch collection data by its ID.
   * @param {string} id - The collection ID.
   * @param {number} limit - The max number of products in the collection to fetch
   * @returns {Promise<Object>} The processed collection data.
   */
  async getCollectionById(id, limit) {
    const response = await this.query(GetCollectionById, {
      id: `gid://shopify/Collection/${id}`,
      limit,
    })
    return cleanUpCollectionData(response.data)
  }

  /**
   * Fetch products only based on collection ID.
   * @param {string} id - The collection ID.
   * @param {number} limit - The max number of products in the collection to fetch.
   * @returns {Promise<Array>} All products for the collection.
   */
  async getProductsByCollectionId(id, limit, filters) {
    const allProducts = []
    const maxRequests = 10
    let requestCount = 0
    const fetchPage = async (id, limit, cursor) => {
      if (requestCount >= maxRequests) {
        return
      }
      const params = {
        id: `gid://shopify/Collection/${id}`,
        limit,
      }
      if (cursor) params.afterCursor = cursor
      const response = await this.query(GetProductsByCollectionId, params)
      const products = cleanUpCollectionData(response.data).products
      allProducts.push(...products)
      const pageInfo = response.data.node.products.pageInfo
      if (pageInfo.hasNextPage) {
        requestCount++
        await fetchPage(id, limit, pageInfo.endCursor)
      }
    }
    await fetchPage(id, limit)
    return allProducts
  }

  /**
   * Fetch batch of collection data by their IDs.
   * @param {string} ids - An array of the collection IDs.
   * @returns {Promise<Object>} The processed collections data.
   */
  async getCollectionsByBatch(ids) {
    const response = await this.query(GetCollectionsByBatch, {
      ids: ids.map((id) => `gid://shopify/Collection/${id}`),
    })
    return response.data.nodes
      .filter(Boolean)
      .map((collection) => cleanUpCollectionData({ node: collection }))
  }

  /**
   * Fetch collections based on an array of titles.
   *
   * @function GetCollectionsByTitles
   * @param {string[]} titles - An array of collection titles to search for.
   * @returns {object[]} An array of collections matching the provided titles.
   */
  async getCollectionsByTitles(titles) {
    const titlesQuery = titles.map((title) => `title:${title}`).join(' OR ')
    const response = await this.query(GetCollectionsByTitles, {
      titlesQuery,
    })
    return response.data.collections.edges
      .filter(Boolean)
      .map((collection) => cleanUpCollectionData(collection))
  }

  /**
   * Search products.
   * @param {string} query - The search query.
   * @returns {Promise<Array<Object>>} The list of matched products.
   */
  async searchProducts(query, limit = 250) {
    const response = await this.query(SearchProducts, { query, limit })
    return cleanUpSearchData(response.data)
  }
}

/**
 * Fetches the product recommendations for a given product.
 *
 * @param {string} productId - The product ID.
 * @param {number} limit - The max number of products to fetch
 * @param {string} intent - The recommendation intent.
 * @returns {Promise<Object>} The processed product data.
 */
const getProductRecommendations = async (
  productId,
  limit = 10,
  intent = 'related'
) => {
  const response = await fetch(
    `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`
  )
  const { products } = await response.json()
  return products
}

/**
 * Strips GraphQL Global ID (GID) down to its numeric component.
 * @param {string} value - The GID.
 * @returns {string} The stripped GID.
 */
function stripGID(value) {
  const gidPattern = /^gid:\/\/shopify\/\w+\//
  if (gidPattern.test(value)) {
    return value.replace(gidPattern, '')
  }
  return value
}

/**
 * Makes metafield data from GraphQL workable with JS.
 * @param {Array<Object>} rawMetafields - The GraphQL response for metafields.
 * @returns {Object} The processed metafield data.
 */
function cleanUpMetafields(rawMetafields) {
  if (!rawMetafields) return {}
  return rawMetafields
    .filter((m) => m !== null)
    .reduce((acc, metafield) => {
      if (!acc[metafield.namespace]) {
        acc[metafield.namespace] = {}
      }
      let value
      if (Object.keys(metafield.reference || {}).includes('image')) {
        value = metafield.reference.image.originalSrc
      } else if (Object.keys(metafield.reference || {}).includes('url')) {
        value = metafield.reference.url
      } else if (isStringifiedArray(metafield.value)) {
        const array = JSON.parse(metafield.value)
        value = array.map((i) => stripGID(i))
      } else {
        value = stripGID(metafield.value)
      }
      acc[metafield.namespace][metafield.key] = value
      return acc
    }, {})
}

/**
 * Makes product data from GraphQL workable with JS.
 * @param {Object} rawData - The GraphQL response for product.
 * @returns {Object|null} The processed product data.
 */
function cleanUpProductData(rawData) {
  if (!rawData || !rawData.node) {
    return null
  }

  const rawProduct = rawData.node

  const variantsSortedByPrice =
    rawProduct.variants?.edges.sort((a, b) => {
      const priceA = parseFloat(a.node.price.amount)
      const priceB = parseFloat(a.node.price.amount)
      if (priceA < priceB) return -1
      if (priceA > priceB) return 1
      return 0
    }) || []
  const lowestPriceVariant = variantsSortedByPrice[0]
  let compareAtPrice = null
  if (variantsSortedByPrice.length) {
    const compareAtPriceProperty =
      lowestPriceVariant.node.compareAtPrice?.amount
    if (compareAtPriceProperty)
      compareAtPrice = parseFloat(compareAtPriceProperty)
  }

  return {
    id: stripGID(rawProduct.id),
    available: rawProduct.availableForSale,
    title: rawProduct.title,
    handle: rawProduct.handle,
    description: rawProduct.descriptionHtml,
    minPrice: parseFloat(rawProduct.priceRange?.minVariantPrice?.amount),
    maxPrice: parseFloat(rawProduct.priceRange?.maxVariantPrice?.amount),
    compareAtPrice,
    handle: rawProduct.handle,
    type: rawProduct.productType,
    vendor: rawProduct.vendor,
    createdAt: new Date(rawProduct.createdAt),
    updatedAt: new Date(rawProduct.updatedAt),
    publishedAt: new Date(rawProduct.publishedAt),
    onlineStoreUrl: rawProduct.onlineStoreUrl,
    tags: rawProduct.tags,
    images: rawProduct.images?.edges.map((edge) => edge.node),
    options: rawProduct.options,
    collections: rawProduct.collections?.edges.map((edge) => {
      return cleanUpCollectionData(edge)
    }),
    variants: rawProduct.variants?.edges.map((edge) => {
      return {
        id: stripGID(edge.node.id),
        title: edge.node.title,
        productTitle: rawProduct.title,
        price: parseFloat(edge.node.price.amount),
        compareAtPrice: parseFloat(edge.node.compareAtPrice?.amount),
        currencyCode: edge.node.price.currencyCode,
        availableForSale: edge.node.availableForSale,
        image: edge.node.image,
        productImages: rawProduct.images?.edges.map((edge) => edge.node),
        sku: edge.node.sku,
        selectedOptions: edge.node.selectedOptions,
        quantityAvailable: edge.node.quantityAvailable,
        metafields: cleanUpMetafields(edge.node.metafields),
        handle: rawProduct.handle,
        createdAt: new Date(rawProduct.createdAt),
      }
    }),
    metafields: cleanUpMetafields(rawProduct.metafields),
    isDiscounted: rawProduct.variants?.edges.some((variantEdge) => {
      const variant = variantEdge.node
      const originalPrice = parseFloat(variant.price.amount)
      const compareAtPrice = parseFloat(variant.compareAtPrice?.amount)
      return compareAtPrice > originalPrice
    }),
  }
}

/**
 * Process the search data response.
 * @param {Object} rawData - The raw search data.
 * @returns {Array<Object>} The list of processed product data.
 */
function cleanUpSearchData(rawData) {
  if (!rawData) {
    return null
  }
  return rawData.products.edges.map((product) => cleanUpProductData(product))
}

/**
 * Makes collection data from GraphQL workable with JS.
 * @param {Object} rawData - The GraphQL response for collection.
 * @returns {Object|null} The processed collection data.
 */
function cleanUpCollectionData(rawData) {
  if (!rawData || !rawData.node) {
    return null
  }

  const rawCollection = rawData.node

  return {
    id: stripGID(rawCollection.id),
    title: rawCollection.title,
    handle: rawCollection.handle,
    description: rawCollection.descriptionHtml,
    updatedAt: new Date(rawCollection.updatedAt),
    publishedAt: new Date(rawCollection.publishedAt),
    image: rawCollection.image,
    products: rawCollection.products?.edges.map((edge) => {
      return cleanUpProductData(edge)
    }),
    metafields: cleanUpMetafields(rawCollection.metafields),
  }
}

/**
 * Checks whether a value represents a valid number.
 * @example
 * validateNumber(NaN)       // returns false
 * validateNumber(undefined) // returns false
 * validateNumber(1)         // returns true
 * validateNumber(1.1)       // returns true
 * validateNumber('1')       // returns true
 * validateNumber('1.1')     // returns true
 * validateNumber('$1.00')   // returns false (preformatted price needs a different check)
 *
 * @param {*} value - The value to check.
 * @returns {boolean} - True if value is a valid number, false otherwise.
 */
const validateNumber = (value) => Number.isFinite(parseFloat(value))

/**
 * Converts number representing a price in minor unit (e.g. cents) to major unit (e.g. dollars).
 *
 * @param {number|string} priceInMinorUnit - The price in minor unit.
 * @returns {number|string|false} - The price in major unit or false.
 */
function minorToMajorUnit(priceInMinorUnit) {
  if (!validateNumber(priceInMinorUnit)) return false
  return (priceInMinorUnit / 100).toFixed(2)
}

/**
 * Simple formatter to convert price to string.
 *
 * @param {number|string} priceInMinorUnit - The price in minor unit.
 * @param {string} [currencyUnit='$'] - The currency symbol.
 * @returns {string} - Formatted price string.
 */
function formatPrice(priceInMinorUnit, currencyUnit = '$') {
  const priceInMajorUnit = minorToMajorUnit(priceInMinorUnit)
  if (!priceInMajorUnit) return ''
  return `${currencyUnit}${priceInMajorUnit}`
}

/**
 * Converts string in snake case to camel case.
 *
 * @param {string} string - String to convert.
 * @returns {string} - Converted string in camel case.
 */
function snakeToCamel(string) {
  return string.replace(/(_\w)/g, (matches) => matches[1].toUpperCase())
}

/**
 * Simple debounce function that prevents excessive function calls.
 *
 * @param {Function} func - Function to debounce.
 * @param {number} delay - Interval within which call cannot be re-executed.
 * @returns {Function} - Debounced function.
 */
function debounce(func, delay) {
  let timer
  return function () {
    const context = this
    const args = arguments
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(context, args)
    }, delay)
  }
}

/**
 * Checks if a given string represents a valid stringified array.
 *
 * @param {string} string - The string to check.
 * @returns {boolean} - Returns true if the string is a valid stringified array, otherwise false.
 */
function isStringifiedArray(string) {
  if (typeof string !== 'string') return false
  if (string.startsWith('[') && string.endsWith(']')) {
    try {
      const potentialArray = JSON.parse(string)
      return Array.isArray(potentialArray)
    } catch (error) {
      return false
    }
  }
  return false
}

/**
 * Adds one or more product variants to the Shopify cart.
 *
 * @async
 * @function
 * @param {Array|Object} items - An array of objects or a single object representing the items to add to the cart.
 * Each object should have an `id` property (variant ID) and a `quantity` property.
 * @param {boolean} [openCart=true] - Whether to trigger the cart to open after adding items.
 * @throws Will throw an error if the fetch operation encounters a problem.
 * @example
 * addToCart({ id: '1234567890', quantity: 1 });
 * addToCart([{ id: '1234567890', quantity: 1 }, { id: '0987654321', quantity: 2 }]);
 */
const addToCart = async function (items, openCart = true) {
  if (!Array.isArray(items)) {
    items = [items]
  }
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    })
    if (!response.ok) {
      throw new Error('Network response was not ok')
    }
    await response.json()

    if (openCart) {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }))
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }))
    }
  } catch (error) {
    console.error(
      'There was a problem with the fetch operation:',
      error.message
    )
  }
}

/**
 * Interpolates a template string with provided replacements.
 *
 * @param {string} template - The string template with placeholders.
 * @param {Object} replacements - An object containing key-value pairs for placeholders and their replacements.
 * @returns {string} The interpolated string.
 *
 * @example
 *   const messageTemplate = 'Hello, {name}! You have {count} messages.';
 *   const values = {
 *     name: 'Alice',
 *     count: 5
 *   }
 *   const message = interpolate(messageTemplate, values);
 *   console.log(message); // "Hello, Alice! You have 5 messages."
 */
function interpolate(template, replacements) {
  return template.replace(/\{(\w+)\}/g, (_, placeholder) => {
    return replacements[placeholder] || _
  })
}

/**
 * Finds and returns a slotted child within a Web Component
 *
 * @param {DOMElement} shadowRoot
 * @param {String} name
 * @param {Boolean} firstOnly
 * @returns DOMElement | Empty
 */
function getChildSlot(shadowRoot = null, name = '', firstOnly = false) {
  const children =
    shadowRoot
      ?.querySelector('slot')
      ?.assignedElements({ flatten: true })
      .filter((node) => {
        return node.matches(name)
      }) || []
  return firstOnly ? (children || [])[0] : children
}

/**
 * A shorthand for querySelector
 *
 * @param {String} selector
 * @param {DomElement} parent
 * @returns DOMElement | Empty
 */
function one(selector, parent) {
  parent || (parent = document)
  return parent.querySelector(selector)
}

/**
 * A shorthand for querySelectorAll
 *
 * @param {String} selector
 * @param {DomElement} parent
 * @returns DOMElement | Empty
 */
function all(selector, parent) {
  parent || (parent = document)
  var selection = parent.querySelectorAll(selector)
  return Array.prototype.slice.call(selection)
}

/**
 * Toggles an array of classes on a DOMElement
 *
 * @param {DOMElement} element
 * @param {Array} classes
 */
function toggle(element, classes = []) {
  classes.forEach((_class) => {
    element.classList.toggle(_class)
  })
}

/**
 * Not lovely & not pure; attempts to pause all currently
 * playing media (ie. videos)
 */
const pauseAllMedia = () => {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*'
    )
  })
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*')
  })
  document.querySelectorAll('video').forEach((video) => video.pause())
  document
    .querySelectorAll('product-model')
    .forEach((model) => model.modelViewerUI?.pause())
}

/**
 * A classic throttle helper
 *
 * @param {Function} callback
 * @param {Number} limit
 * @returns
 */
const throttle = (callback, limit) => {
  let waiting = false
  return function () {
    if (!waiting) {
      callback.apply(this, arguments)
      waiting = true
      setTimeout(function () {
        waiting = false
      }, limit)
    }
  }
}

/**
 * Serializes form
 *
 * @param {Form} form
 * @returns String
 */
const serializeForm = (form) => {
  const obj = {}
  const formData = new FormData(form)

  for (const key of formData.keys()) {
    // ensures that the line item properties are added as an object, instead of as an array
    const regex = /(?:^(properties\[))(.*?)(?:\]$)/

    if (regex.test(key)) {
      obj.properties = obj.properties || {}
      obj.properties[regex.exec(key)[2]] = formData.get(key)
    } else {
      obj[key] = formData.get(key)
    }
  }

  return JSON.stringify(obj)
}

/**
 * Returns a generic fetch config object (XHR)
 *
 * @param {String} type
 * @param {String} method
 * @returns Object
 */
const fetchConfig = (type = 'json', method = 'POST') => {
  return {
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  }
}

/**
 * Checks to see if the device supports touch gestures.
 */
const isTouch = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints
}

/**
 * Clones a complex Javascript object. This is a
 * thorough way to ensure that the object is fully
 * cloned with no references kept in place.
 *
 * @param {Object} obj
 */
const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj))
}

/**
 * Removes whitespace within a string and makes all
 * characters lowercase.
 *
 * @param {String} str
 */
const handleize = (str) => str.replace(/[ /_]/g, '-').toLowerCase()

/**
 * Decodes a string that has been encoded through the 'url_encode' Shopify filter
 * @param {*} str
 */
const decode = (str) => decodeURIComponent(str).replace(/\+/g, ' ')

/**
 * Returns an iFrame src URL value for either YouTube or Vimeo hosted videos
 *
 * @param {string} host 'youtube' or 'vimeo'
 * @param {string} id the external video ID
 * @param {boolean} autoplay whether the video should autoplay
 * @param {boolean} controls whether the video should show controls
 * @param {boolean} loop whether the video should loop
 */
const externalVideoEmbedSrc = (
  host = 'youtube',
  id,
  autoplay = false,
  controls = false,
  loop = false
) => {
  let src = ''
  const queryParam = (param, value) => {
    return `${param}=${Number(value)}`
  }
  const autoplayParam = queryParam('autoplay', autoplay)
  const controlsParam = queryParam('controls', controls)
  const loopParam = queryParam('loop', loop)
  const queryParams = `${autoplayParam}&${controlsParam}&${loopParam}`

  switch (host) {
    case 'youtube':
      src = `https://www.youtube.com/embed/${id}?${queryParams}&enablejsapi=1&modestbranding=1&mute=1&cc_load_policy=0&disablekb=0&fs=1&iv_load_policy=1&playsinline=1&rel=1&widgetid=1`
      break
    case 'vimeo':
      src = `https://player.vimeo.com/video/${id}?${queryParams}&byline=0&playsinline=1&title=0`
      break
    default:
      console.error('Invalid host value (must be "youtube" or "vimeo")')
  }
  return src
}

/**
 * Gets the previous sibling of an element that matches a given selector
 *
 * @param {HTMLElement} element the element to start from
 * @param {string} selector the selector to match
 */
const getPreviousSibling = (element, selector) => {
  let sibling = element.previousElementSibling

  if (!selector) return null

  while (sibling) {
    if (sibling.matches(selector)) {
      return sibling
    }
    sibling = sibling.previousElementSibling
  }
}

const t = (namespace, fallback, params) => {
  let message = window.langMessages[namespace] ? window.langMessages[namespace] : fallback

  // Replace placeholders with actual parameter valu
  if (!params) return message

  Object.keys(params).forEach(key => {
    const value = params[key]
    const regex = new RegExp(`{{${key}}}`, 'g')
    message = message.replace(regex, value)
  })

  return message
}

const generateSrcSet = (media) => {
  const widths = ['200', '300', '400', '600', '800', '1000', '1200', '1400', '1600']
  return widths.map(width => `${media.url}?width=${width} ${width}w`).join(', ')
}

const formatCurrency = (number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number)
}


export {
  StorefrontClient,
  getProductRecommendations,
  cleanUpProductData,
  cleanUpCollectionData,
  validateNumber,
  minorToMajorUnit,
  formatPrice,
  snakeToCamel,
  debounce,
  addToCart,
  interpolate,
  getChildSlot,
  one,
  all,
  toggle,
  pauseAllMedia,
  throttle,
  serializeForm,
  fetchConfig,
  isTouch,
  deepClone,
  handleize,
  decode,
  externalVideoEmbedSrc,
  getPreviousSibling,
  generateSrcSet,
  t,
  formatCurrency
}
